import type {
  GeoblockMode,
  GeoCountry,
  GeoCountryGroup,
} from "./geoblock.types";
import type { CarouselRow } from "../constants/carouselRow";
import type languageCodeList from "../constants/languages";
import type { HorizontalImageFormat, ImageFormat } from "../constants/ratio";
import type { Role } from "../constants/roles";
import type { Abilities, Indexes, Page, TranslationsIndexes } from "../types";

export interface ProjectsResponse {
  data: Project[];
  indexes: ProjectsIndexes;
  page: ProjectsPage;
}

export interface GetProjectListParams {
  uuids?: string[];
}

export type GetProjectResponse = Project;

export type LanguageCode = (typeof languageCodeList)[number];

export interface Project {
  abilities: Abilities;
  active_offers: number;
  active_users: number;
  base_url: string;
  churn: number;
  contents: number;
  domain_alias: null | string;
  enabled: boolean;
  inserted_at: string;
  locales: Locales;
  name: string;
  page_links: PageLink[];
  registered_users: number;
  roles: Role[];
  social_network_links: SocialNetworkLink[];
  storefront_project_logins: StorefrontProjectLogin[];
  storefront_project_keys: StorefrontProjectKey[];
  storefront_project_params: StorefrontProjectParams;
  storefront_project_payment_creds: StorefrontProjectPaymentCreds[] | null;
  trackers: Tracker[];
  translations: ProjectTranslation[];
  translations_indexes: TranslationsIndexes;
  trialing_users: number;
  triggers: Trigger[];
  type: TypeEnum;
  unsubscribe_reasons: UnsubscribeReasons | null;
  updated_at: string;
  uuid: string;
}

export type UpdatedProjectTranslation = Omit<ProjectTranslation, "uuid">;
export interface UpdatedPageLinks extends Omit<
  PageLink,
  "translations" | "uuid" | "translations_indexes"
> {
  translations: Omit<PageLinkTranslation, "uuid">[];
  uuid?: string;
  translations_indexes?: PageLinkTranslationsIndexes;
}

export type UpdatedStorefrontProjectLogin = Omit<
  StorefrontProjectLogin,
  "uuid"
>;

export type UpdatedSocialNetworkLink = Omit<SocialNetworkLink, "uuid">;

export interface UpdatedTracker extends Omit<Tracker, "uuid" | "config"> {
  config: Partial<TrackerConfig>;
}
export interface UpdatedProject extends Omit<
  Project,
  | "translations"
  | "page_links"
  | "storefront_project_logins"
  | "trackers"
  | "social_network_links"
> {
  translations: (UpdatedProjectTranslation | ProjectTranslation)[];
  page_links: (UpdatedPageLinks | PageLink)[];
  storefront_project_logins: (
    | StorefrontProjectLogin
    | UpdatedStorefrontProjectLogin
  )[];
  trackers: (Tracker | UpdatedTracker)[];
  social_network_links: (SocialNetworkLink | UpdatedSocialNetworkLink)[];
}

export interface Locales {
  en?: En;
  fr?: Fr;
  ar?: Ar;
  co?: Co;
  de?: Co;
  it?: Co;
  pt?: Pt;
  es?: Es;
  ja?: Ja;
}

export interface Ar {
  login: ArLogin;
}

export interface ArLogin {
  custom_1?: string;
  custom_2: string;
  custom_3: string;
}

export interface Co {
  login: CoLogin;
}

export interface CoLogin {
  custom_1: string;
  custom_2?: string;
  custom_3?: string;
}

export interface En {
  login: EnLogin;
  checkout?: EnCheckout;
  content?: EnContent;
  edu?: Edu;
  navbar?: EnNavbar;
  profile?: EnProfile;
}

export interface EnCheckout {
  alreadyHaveOffer?: string;
  buttonFreeCopy: string;
  errorValidationToEmail?: string;
  fromNameContent?: string;
  giftSubtitle?: string;
  giftTokenContent?: string;
  noOffers?: string;
  resultGiftMarkdown: string;
  resultGiftOkBtn: string;
  resultSvodMarkdown: string;
  resultSvodOkBtn: string;
  startDateLabel: string;
  toEmailContent: string;
  toNameContent?: string;
  to?: string;
  toEmail?: string;
  buttonRent?: string;
  freecopySucceeded?: string;
  giftActivateSubsubtitle?: string;
  giftFailed?: string;
}

export interface EnContent {
  casting: string;
  "no-results": string;
  "no-videos": string;
  other: string;
  titleRelatedContentsAsActor: string;
  buy?: string;
  contents?: string;
  our_selection?: string;
  show_more?: string;
  titleRelatedContentsAsDirector?: string;
  casting_plural?: string;
}

export interface Edu {
  oauthRedirectBtn: string;
  oauthRedirectSubtext: string;
  oauthRedirectText: string;
}

export interface EnLogin {
  custom_1?: string;
  custom_2?: string;
  custom_3?: string;
  acceptTermsText?: string;
  askLogin?: string;
  badUsernameOrPassword?: string;
  errorValidationEmail?: string;
  freecopyEmailContent?: string;
  freecopyLoginTitle?: string;
  giftEmailContent?: string;
  giftLoginTitle?: string;
  giftRegisterTitle?: string;
  resetPasswordTitle?: string;
  successResetPassword?: string;
  errorEmptyEmail?: string;
  errorEmptyPassword?: string;
  errorFacebookAuth?: string;
  freecopyLoginSubSubtitle?: string;
  freecopyRegisterSubSubtitle?: string;
  freecopyRegisterTitle?: string;
  mailAlreadyUsed?: string;
  shouldHaveAccount?: string;
  emailContent?: string;
  freecopyLoginSubtitle?: string;
  giftLoginSubtitle?: string;
  subscribeEmailContent?: string;
  successNewPassword?: string;
}

export interface EnNavbar {
  offer: string;
  subscribelogged: string;
  subscribe?: string;
}

export interface EnProfile {
  cancelButton: string;
  confirmUnsubscribe: string;
  emailContent: string;
  emailLabel: string;
  errorValidationEmail: string;
  firstnameLabel: string;
  lastnameLabel: string;
  noOffers: string;
  offers: string;
  oldPasswordLabel: string;
  subscriptionCanceled: string;
  subscriptions: string;
  unsubscribe: string;
  unsubscribeText: string;
  validateUnsubscribe: string;
  avatarUpdated?: string;
}

export interface Es {
  navbar: EsNavbar;
  login?: EnLogin;
}

export interface EsNavbar {
  subscribe: string;
}

export interface Fr {
  login: EnLogin;
  checkout?: EnCheckout;
  content?: FrContent;
  edu?: Edu;
  profile?: FrProfile;
  navbar?: FrNavbar;
}

export interface FrContent {
  actor?: string;
  actor_plural?: string;
  author?: string;
  author_plural?: string;
  casting: string;
  casting_plural?: string;
  contact?: string;
  contact_plural?: string;
  director?: string;
  director_plural?: string;
  producer?: string;
  producer_plural?: string;
  rent?: string;
  sale?: string;
  sale_plural?: string;
  titleRelatedContentsAsActor: string;
  buy?: string;
  contents?: string;
  "no-results"?: string;
  "no-videos"?: string;
  our_selection?: string;
  show_more?: string;
}

export interface FrNavbar {
  myVideos: string;
  subscribe?: string;
}

export interface FrProfile {
  cancelButton: string;
  subscriptionCanceled: string;
  unsubscribe: string;
  unsubscribeText: string;
  validateUnsubscribe: string;
  billing?: string;
  confirmUnsubscribe?: string;
  emailLabel?: string;
  errorValidationEmail?: string;
  noOffers?: string;
}

export interface Ja {
  checkout: JaCheckout;
  content: JaContent;
  login: Record<string, string>;
  navbar: JaNavbar;
  tabbar: Tabbar;
}

export interface JaCheckout {
  subscription_duration: string;
  subscription_quality: string;
}

export interface JaContent {
  titleMyList: string;
  titleResumeMedias: string;
}

export interface JaNavbar {
  my_medias: string;
}

export interface Tabbar {
  my_list: string;
}

export interface Pt {
  login: PtLogin;
}

export interface PtLogin {
  custom_1: string;
  custom_2: string;
  custom_3: string;
}

export interface PageLink {
  translations: PageLinkTranslation[];
  translations_indexes: PageLinkTranslationsIndexes;
  uuid: string;
}

export interface PageLinkTranslation {
  default: boolean;
  language: LanguageCode;
  title: string;
  url: string;
  uuid: string;
}

export interface PageLinkTranslationsIndexes {
  default: Default;
  language: PurpleLanguage;
}

export interface Default {
  true: number[];
  false?: number[];
}

export interface PurpleLanguage {
  fr: number[];
  de?: number[];
  mn?: number[];
  en?: number[];
}

export interface SocialNetworkLink {
  social_network: string;
  uid: string;
  uuid: string;
}

export interface StorefrontProjectLogin {
  config: StorefrontProjectLoginConfig;
  login_provider: string;
  uuid: string;
}

export interface StorefrontProjectLoginConfig {
  id?: string;
  secret?: string;
  client_id?: string;
  client_secret?: string;
}

export interface StorefrontProjectKey {
  secret_key: string;
  service: Service;
  status: Status;
}

export enum Service {
  Ikast = "ikast",
  Okast = "okast",
  Zapier = "zapier",
}

export enum Status {
  Enabled = "enabled",
}

export type PeriodLabel = "week" | "twoWeeks" | "month";

export interface StorefrontProjectParams {
  allow_chat_live: boolean;
  allow_media_resume: boolean;
  playlists_type: "disabled" | "multiple" | "single";
  allow_player_mirror: boolean;
  allow_preview: boolean;
  allow_sticky_footer: boolean;
  allow_stripe_portal: boolean;
  allow_upnext: boolean;
  comment_abuse_email: null | string;
  comments_offline_visibility: boolean;
  comments_validation_visibility: boolean;
  countries: GeoCountry[];
  country_groups: GeoCountryGroup[];
  geoblock_mode: GeoblockMode;
  geoblock_redirect_url: string | null;
  display_free_label: boolean;
  display_live_label: boolean;
  display_replay_label: boolean;
  display_top_label: boolean;
  display_last_chance_label: boolean;
  display_tvod_label: boolean;
  display_coming_soon_label: boolean;
  period_top_label: "week" | "month" | "threeMonths" | "year" | "all";
  period_new_label: PeriodLabel;
  period_last_chance_label: PeriodLabel;
  period_coming_soon_label: PeriodLabel;
  display_new_label: boolean;
  display_social_sharing_buttons: boolean;
  display_social_network_sharing: boolean;
  display_user_country: boolean;
  display_user_custom_1: boolean;
  display_user_custom_2: boolean;
  display_user_custom_3: boolean;
  display_user_language: boolean;
  enable_casting: boolean;
  enable_comments_media: boolean;
  enable_comments_smartlist: boolean;
  enable_gift_button: boolean;
  livechat_display_user_count: boolean;
  sessions_max: number;
  require_registration_before_offer: boolean;
  user_custom_values: UserCustomValues | null;
  offers_button_destination: "all" | "subscriptions" | "purchases";
}

export interface UserCustomValues {
  custom_1: Record<string, string[]> | string;
  custom_2: Custom | null | string;
  custom_3: Custom | null | string;
}

export interface Custom {
  fr: string[];
  en?: string[];
}

export type StorefrontProjectPaymentCreds =
  | StripeStorefrontProjectPaymentCreds
  | PaypalStorefrontProjectPaymentCreds;

export interface StripeStorefrontProjectPaymentCreds {
  credentials: StripeCredentials;
  payment_provider: "stripe";
  uuid: string;
}

export interface StripeCredentials {
  access_token: string;
  livemode?: boolean;
  refresh_token: string;
  scope?: Scope;
  stripe_publishable_key: string;
  stripe_user_id: string;
  token_type?: TokenType;
}

export interface PaypalStorefrontProjectPaymentCreds {
  credentials: PaypalCredentials;
  payment_provider: "paypal";
  uuid: string;
}

export interface PaypalCredentials {
  accountStatus: string;
  consentStatus: boolean;
  isEmailConfirmed: boolean;
  merchantId: string;
  merchantIdInPayPal: string;
  permissionsGranted: boolean;
  productIntentId: string;
  authorization_code: string;
  riskStatus: string;
  sfp_uuid: string;
}

export enum Scope {
  ReadWrite = "read_write",
}

export enum TokenType {
  Bearer = "bearer",
}

export interface Tracker {
  config: TrackerConfig;
  type: string;
  uuid: string;
}

export interface TrackerConfig {
  id: string;
  siteUrl?: string;
}

export interface ProjectTranslation {
  copyright: null | string;
  default: boolean;
  description: null | string;
  favicon_url: null | string;
  language: LanguageCode;
  logo_url: null | string;
  pictures: Pictures | null;
  title: null | string;
  uuid: string;
  logo?: string | ArrayBuffer | null;
  favicon?: string | ArrayBuffer | null;
  cover_picture_16_6?: string | ArrayBuffer | null;
}

export interface Pictures {
  cover_picture_16_6: CoverPicture;
  cover_picture_16_9: CoverPicture;
  cover_picture_1_1: CoverPicture;
  cover_picture_3_4: CoverPicture;
}

export interface CoverPicture {
  high: string;
  low: string;
  medium: string;
}

export interface Trigger {
  email: boolean;
  event_name: EventName;
  zapier: number;
}

export enum EventName {
  CancelSubscription = "cancel_subscription",
  CommentAbuse = "comment_abuse",
  CreatePassword = "create_password",
  DeleteAccount = "delete_account",
  ForgotPassword = "forgot_password",
  FreeCopy = "free_copy",
  FreecopyActivation = "freecopy_activation",
  Gift = "gift",
  GiftConfirmation = "gift_confirmation",
  LiveStart = "live_start",
  LiveStop = "live_stop",
  PaymentConfirmation = "payment_confirmation",
  PaymentFailed = "payment_failed",
  PaymentIncomplete = "payment_incomplete",
  PurchaseRent = "purchase_rent",
  Registration = "registration",
  Subscription = "subscription",
}

export enum TypeEnum {
  Edu = "edu",
  Standard = "standard",
}

export interface UnsubscribeReasons {
  reasons: Reason[];
}

export interface Reason {
  name: string;
  translations: Translations;
}

export type Translations = Record<string, string>;

export interface ProjectsIndexes extends Indexes {
  type: TypeClass;
}

export interface TypeClass {
  edu: number[];
  standard: number[];
}

export interface ProjectsPage {
  perPage: number;
  total: number;
  current: number;
}

export interface GetLanguagesResponse {
  data: Language[];
  indexes: Indexes;
  page: Page;
  type: "language";
}

export interface GetComponentListResponse<
  T extends GetComponentResponse = GetComponentResponse,
> {
  data: T[];
  indexes: GetComponentListIndexes;
  page: Page;
  type: "component";
}

interface GetComponentListIndexes extends Indexes {
  platform: Record<Platform, number[]>;
  section: Record<Section, number[]>;
}

// TODO add other components types
export type GetComponentResponse =
  | FAQComponent
  | ShortcutComponent
  | HighlightComponent
  | CarouselComponent
  | WidgetComponent
  | MosaicComponentWithoutIndividualCarousel
  | ImageFormatComponent
  | ShareComponent
  | HeaderComponent
  | FooterComponent;

export interface Language {
  language: string;
  uuid: string;
}

export interface GetCountriesResponse {
  data: Country[];
  indexes: Indexes;
  page: Page;
  type: "country";
}

export interface Country {
  country: string;
  uuid: string;
}

export interface CountryGroupCountry {
  uuid: string;
  alpha2: string;
}

export interface CountryGroup {
  uuid: string;
  label: string;
  base_group: boolean;
  organization_uuid: string | null;
  countries: CountryGroupCountry[];
  inserted_at: string;
  updated_at: string;
}

export interface GetCountryGroupListResponse {
  data: CountryGroup[];
  indexes: Indexes;
  page: Page;
  type: "country_group";
}

export interface CountryGroupPayload {
  label: string;
  countries: { uuid: string }[];
}

export interface CreateCountryGroupResponse {
  uuid: string;
}

export type SearchPagePlatformComponent =
  | SearchPlaceholderComponent
  | SearchComponent
  | CarouselComponent
  | MosaicComponentWithoutIndividualCarousel;

export type PlatformComponent =
  | WidgetComponent
  | ShareComponent
  | CarouselComponent
  | MosaicComponent
  | ImageFormatComponent
  | HighlightComponent
  | SearchPlaceholderComponent
  | SearchComponent
  | SmartlistDataSourceComponent
  | FAQComponent
  | ShortcutComponent
  | HeaderComponent
  | FooterComponent;

export type ContentPagePlatformComponent =
  | WidgetComponent
  | ShareComponent
  | CarouselComponent
  | ContentPageImageFormatComponent;

export type PositionComponent = "left" | "center" | "right";

export type FooterComponentType =
  | "text"
  | "lang"
  | "page_links"
  | "social_links";

export type HeaderComponentType =
  | "logo"
  | "user"
  | "categories"
  | "lang"
  | "search_bar"
  | "login"
  | "register"
  | "gift"
  | "page_list"
  | "offer";

interface HeaderComponentBase extends CommonComponent {
  type: HeaderComponentType;
  section: "header";
}

interface FooterComponentBase extends CommonComponent {
  type: FooterComponentType;
  section: "footer";
}

export interface HeaderFooterCommonComponentParams {
  position?: PositionComponent;
}

export interface LogoComponent extends HeaderComponentBase {
  type: "logo";
  params: HeaderFooterCommonComponentParams;
}

export interface ProfileComponent extends HeaderComponentBase {
  type: "user";
  params: HeaderFooterCommonComponentParams;
}

export interface OfferComponent extends HeaderComponentBase {
  type: "offer";
  params: OfferComponentParams;
}

export type OfferComponentDestination = "all" | "subscriptions" | "purchases";

export interface OfferComponentParams extends HeaderFooterCommonComponentParams {
  destination: OfferComponentDestination;
}

export type HeaderFooterComponentParams =
  | HeaderFooterCommonComponentParams
  | CategoriesComponentParams;

export interface CategoriesComponentParams extends HeaderFooterCommonComponentParams {
  show_my_medias: boolean;
  show_my_playlists: boolean;
  smartlist_uuid: string;
}

export interface CategoriesComponent extends HeaderComponentBase {
  type: "categories";
  params: CategoriesComponentParams;
}

export interface LanguagesComponentFooter extends FooterComponentBase {
  type: "lang";
  params: HeaderFooterCommonComponentParams;
}

export interface LanguagesComponentHeader extends HeaderComponentBase {
  type: "lang";
  params: HeaderFooterCommonComponentParams;
}

export interface SearchBarComponent extends HeaderComponentBase {
  type: "search_bar";
  params: HeaderFooterCommonComponentParams;
}

export interface LoginComponent extends HeaderComponentBase {
  type: "login";
  params: HeaderFooterCommonComponentParams;
}

export interface RegisterComponent extends HeaderComponentBase {
  type: "register";
  params: HeaderFooterCommonComponentParams;
}

export interface GiftComponent extends HeaderComponentBase {
  type: "gift";
  params: HeaderFooterCommonComponentParams;
}

export interface PageListComponent extends HeaderComponentBase {
  type: "page_list";
  params: HeaderFooterCommonComponentParams;
}

export type HeaderComponent =
  | LogoComponent
  | ProfileComponent
  | CategoriesComponent
  | LanguagesComponentHeader
  | SearchBarComponent
  | LoginComponent
  | RegisterComponent
  | GiftComponent
  | PageListComponent
  | OfferComponent;

export interface CopyrightComponent extends FooterComponentBase {
  type: "text";
  params: HeaderFooterCommonComponentParams;
}

export interface PageLinksComponent extends FooterComponentBase {
  type: "page_links";
  params: HeaderFooterCommonComponentParams;
}

export interface SocialLinksComponent extends FooterComponentBase {
  type: "social_links";
  params: HeaderFooterCommonComponentParams;
}

export type FooterComponent =
  | CopyrightComponent
  | LanguagesComponentFooter
  | PageLinksComponent
  | SocialLinksComponent;

export type HeaderFooterComponent = HeaderComponent | FooterComponent;

export const PLATFORM = {
  ANDROID: "android",
  ANDROID_TV: "androidtv",
  FIRE_TV: "firetv",
  IOS: "ios",
  TVOS: "tvos",
  WEB: "web",
} as const;

export type Platform = (typeof PLATFORM)[keyof typeof PLATFORM];

export const SECTION = {
  CASTING_PAGE: "casting_page",
  COLLECTION_PAGE: "collection_page",
  CONTENT_PAGE: "content_page",
  FOOTER: "footer",
  HEADER: "header",
  PAGE: "page",
  MY_VIDEOS_PAGE: "my_videos_page",
  PLAYLISTS_PAGE: "playlists_page",
  SEARCH_PAGE: "search_page",
  SMARTLIST_PAGE: "smartlist_page",
} as const;

export type Section = (typeof SECTION)[keyof typeof SECTION];

export interface DynamicComponent {
  section_uuid?: string | null;
  smartlist_uuid?: string;
  uuid?: string;
}
export interface CommonComponent {
  dynamic_component: DynamicComponent | null;
  enabled: boolean;
  label: string | null;
  platform: Platform | null;
  rank: number;
  last_rank?: number | null;
  section: Section;
  static_component: StaticComponent | null;
  uuid: string;
}

export interface StaticComponent {
  section_uuid?: string | null;
  smartlist_module_name: SmartlistModuleName;
  uuid?: string;
}

export interface WidgetComponent extends CommonComponent {
  type: "widget";
  storefront_project_page_uuid?: string;
  params: WidgetParams;
}

export interface WidgetParams {
  width: string;
  template_url: string;
  template_temp?: string;
  src_type: "url" | "html_template";
  height: string;
  external_url: string;
  enable_scroll: boolean;
}

export interface ShareComponent extends CommonComponent {
  type: "share";
  params: ShareParams;
}

export interface ImageFormatComponent extends CommonComponent {
  type: "image_format";
  params: ImageFormatParams;
}

export interface ImageFormatParams {
  ratio: ImageFormat;
  isDisabled?: boolean;
}

export interface ContentPageImageFormatComponent extends ImageFormatComponent {
  params: ContentPageImageFormatParams;
}

export interface ContentPageImageFormatParams extends ImageFormatParams {
  trailer?: ContentPageTrailerParams;
}

export interface ContentPageTrailerParams {
  autoplay: boolean;
  autoplay_delay: number | null;
}

export interface ShareParams {
  share_facebook: boolean;
  share_linkedin: boolean;
  share_mail: boolean;
  share_threads: boolean;
  share_twitter: boolean;
  display_custom_id?: boolean;
}

export interface CarouselComponent extends CommonComponent {
  type: "carousel";
  params: CarouselParams;
}
export interface CarouselParams extends CarouselMosaicCommonParams {
  arrows_on_hover?: boolean;
  circular: boolean;
  component_per_slides: number;
  display_arrows: boolean;
  opts_individual_carousel?: OptsIndividualCarousel;
  individual_carousel?: false;
  display_button?: boolean;
  button_horizontal_position?: "left" | "center" | "right";
  button_vertical_position?: "top" | "center" | "bottom";
  autoplay?: boolean;
  interval?: number;
  row?: CarouselRow;
  backgroundColor?: string;
  itemBorderColor?: string;
  withCustomization?: boolean;
}

export interface MosaicComponentWithIndividualCarousel extends CommonComponent {
  type: "mosaic";
  params: MosaicParamsWithIndividualCarousel;
}

export interface MosaicComponentWithoutIndividualCarousel extends CommonComponent {
  type: "mosaic";
  params: MosaicParamsWithoutIndividualCarousel;
}

export interface NowComponentParams {
  themeId: string;
}

export interface MostViewedParams {
  itemType: "media" | "series";
  period: "week" | "month" | "threeMonths" | "year" | "all";
  maxItems: 5 | 10 | 15 | 20;
  hasNumberOnItems: boolean;
  countryId?: string | null;
}

export interface FastNowParams {
  themeId: string;
}

interface SmartlistDataSourceDynamicComponent {
  smartlist_uuid: string;
  uuid: string;
  section_uuid: null;
}
interface SmartlistDataSourceMosaicComponent extends CommonComponent {
  type: "mosaic";
  dynamic_component: SmartlistDataSourceDynamicComponent;
  params:
    | MosaicParamsWithIndividualCarousel
    | MosaicParamsWithoutIndividualCarousel;
}

interface SmartlistDataSourceCarouselComponent extends CommonComponent {
  type: "carousel";
  dynamic_component: SmartlistDataSourceDynamicComponent;
  params: CarouselParams;
}

export type SmartlistDataSourceComponent =
  | SmartlistDataSourceMosaicComponent
  | SmartlistDataSourceCarouselComponent;

interface SimpleModuleCarouselComponent<
  T extends StaticComponent,
> extends CommonComponent {
  type: "carousel";
  dynamic_component: null;
  static_component: T;
  params: CarouselParams;
}

interface SimpleModuleMosaicComponent<
  T extends StaticComponent,
> extends CommonComponent {
  type: "mosaic";
  dynamic_component: null;
  static_component: T & {
    section_uuid: null;
    uuid: undefined;
  };
  params:
    | MosaicParamsWithIndividualCarousel
    | MosaicParamsWithoutIndividualCarousel;
}

type SimpleModule<T extends StaticComponent> =
  | SimpleModuleCarouselComponent<T>
  | SimpleModuleMosaicComponent<T>;

export type MyListComponent = SimpleModule<{
  smartlist_module_name: "my_list";
}>;

export type ResumeComponent = SimpleModule<{
  smartlist_module_name: "resume";
}>;

export type UserRecommendationComponent = SimpleModule<{
  smartlist_module_name: "user_recommendation";
}>;
export type PurchaseComponent = SimpleModule<{
  smartlist_module_name: "purchase";
}>;

export type SimpleModuleComponent =
  | MyListComponent
  | ResumeComponent
  | UserRecommendationComponent
  | PurchaseComponent;

export interface CarouselWithDataSourceParams<T> extends CarouselComponent {
  params: CarouselParams & T;
}

export interface MosaicComponent extends CommonComponent {
  type: "mosaic";
  params:
    | MosaicParamsWithIndividualCarousel
    | MosaicParamsWithoutIndividualCarousel;
}

export type MosaicParams =
  | MosaicParamsWithIndividualCarousel
  | MosaicParamsWithoutIndividualCarousel;

export interface MosaicParamsWithoutIndividualCarousel extends CarouselMosaicCommonParams {
  individual_carousel?: false;
  opts_individual_carousel?: null;
}

export interface MosaicWithDataSourceParams<T> extends MosaicComponent {
  params: MosaicParams & T;
}
export interface MosaicParamsWithIndividualCarousel extends CarouselMosaicCommonParams {
  individual_carousel: true;
  opts_individual_carousel: OptsIndividualCarousel;
}

export type DisplayOrderValue =
  | "random_load"
  | "random_display"
  | "alphabetical"
  | "updated_date"
  | "reverse_ranking"
  | "publication_date";

export interface DisplayOrder {
  enabled: boolean;
  order: DisplayOrderValue;
}

interface CarouselMosaicParams {
  display_dialog: boolean;
  display_item_title: boolean;
  display_title: boolean;
  display_description?: boolean;
  headerPosition?: "left" | "center" | "right";
  ratio: ImageFormat;
  display_order?: DisplayOrder;
  themeId?: string | null;
  display_castings?: boolean;
}

export type CarouselMosaicCommonParams =
  WithConditionsRender<CarouselMosaicParams>;

export interface ConditionRender {
  type: string;
  operator: string;
  predicat: string;
}

export interface OptsIndividualCarousel {
  arrows_on_hover?: boolean;
  circular?: boolean;
  component_per_slides: number;
  display_arrows?: boolean;
  display_button?: boolean;
}

export interface SearchPlaceholderComponent extends CommonComponent {
  platform: null;
  type: "search_placeholder";
  static_component: null;
  dynamic_component: null;
  params: SearchPlaceholderComponentParams;
}

export interface SearchPlaceholderComponentParams {
  placeholders: string[];
}

export interface SearchComponent extends CommonComponent {
  type: "search";
  params: SearchComponentParams;
}

export interface SearchComponentParams {
  [key: number]: {
    field: string;
    type?: string;
  };
  max_results: number;
}

export interface HighlightComponent extends CommonComponent {
  type: "highlight";
  storefront_project_page_uuid: string;
  platform: Platform;
  params: HighlightParams;
}

type WithConditionsRender<T> = T & {
  has_conditions?: boolean;
  conditional_render?: ConditionRender[];
};

export type HighlightParams =
  | WithConditionsRender<HighlightParamsOffer>
  | WithConditionsRender<HighlightParamsHosted>
  | WithConditionsRender<HighlightParamsFastExternal>
  | WithConditionsRender<HighlightParamsLive>;

export interface HighlightParamsOffer {
  offerPlanId: string | null;
  isTitleDisplayed: boolean;
  isDescriptionDisplayed: boolean;
  ratio: HorizontalImageFormat;
  contentType: "offer";
}

export interface HighlightParamsHosted {
  mediaId: string | null;
  trailerId: string | null;
  isTitleDisplayed: boolean;
  isDescriptionDisplayed: boolean;
  isButtonDisplayed: boolean;
  isClickableComponent: boolean;
  isLoopVideo: boolean;
  contentType: "hosted";
}

export interface HighlightParamsFastExternal {
  mediaId: string | null;
  isTitleDisplayed: boolean;
  isDescriptionDisplayed: boolean;
  isButtonDisplayed: boolean;
  isEpgDisplayed: boolean;
  isClickableComponent: boolean;
  contentType: "fast" | "external";
}

export interface HighlightParamsLive {
  mediaId: string | null;
  isTitleDisplayed: boolean;
  isDescriptionDisplayed: boolean;
  isButtonDisplayed: boolean;
  isClickableComponent: boolean;
  contentType: "live";
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PageComponentTypes = {
  FAQ: "faq",
  SHORTCUT: "shortcut",
} as const;

export type PageComponentType =
  (typeof PageComponentTypes)[keyof typeof PageComponentTypes];

export interface PageComponent extends Omit<CommonComponent, "uuid"> {
  section: "page";
  storefront_project_page_uuid: string;
  type: PageComponentType;
  platform: Platform;
  uuid: string;
  label: string;
}

export interface FAQQuestion {
  rank: number;
  question: string;
  answer: string;
  uuid?: string;
}
interface FAQComponentTranslation {
  uuid?: string;
  default: boolean;
  language: string;
  title: string;
  faq_questions: FAQQuestion[];
  cta: {
    label: string;
    subtext: string;
    url: string;
  };
}
export interface FAQComponent extends PageComponent {
  type: "faq";
  params?: WithConditionsRender<{
    questionCount: number;
    withTitle: boolean;
    withCTAButton: boolean;
    withCTASubtitle: boolean;
  }>;
  translations: FAQComponentTranslation[];
  translations_indexes: TranslationsIndexes;
}

export interface ShortcutComponent extends PageComponent {
  type: "shortcut";
  params: WithConditionsRender<{
    metadataSource?: ShortcutComponentMetadataSource;
    withTitle: boolean;
  }>;
}

export type ShortcutComponentMetadataSource =
  | "formats"
  | "new_genres"
  | "themes"
  | "target_audiences";

export type SimpleModuleName =
  | "my_list"
  | "resume"
  | "user_recommendation"
  | "purchase";

type OtherModuleName =
  | "smartlist"
  | "now"
  | "mostViewed"
  | "similar_contents"
  | "collections_contents"
  | "search_casting"
  | "search_fast"
  | "search_smartlist"
  | "search_media"
  | "categories";

export type SmartlistModuleName = SimpleModuleName | OtherModuleName;

export type ComponentWithDataSourceParams<T> =
  | CarouselWithDataSourceParams<T>
  | MosaicWithDataSourceParams<T>;

export type ComponentWithUnknownDataSourceParams =
  | ComponentWithDataSourceParams<
      NowComponentParams | MostViewedParams | FastNowParams
    >
  | SmartlistDataSourceComponent
  | SimpleModuleComponent;

export interface ProjectPageTranslation {
  title: string;
  slug: string;
  default: boolean;
  language: LanguageCode;
  language_uuid?: string;
  uuid?: string;
}

export interface ProjectPage {
  components_platform: Platform[];
  default: boolean;
  enabled: boolean;
  label: string;
  storefront_project_uuid: string;
  uuid: string;
  rank: number;
  translations: ProjectPageTranslation[];
}

export type NewProjectPage = Omit<ProjectPage, "uuid">;

export interface GetProjectPageListResponse {
  data: ProjectPage[];
  indexes: Indexes;
  page: Page;
  type: "storefront_project_pages";
}

export type GetProjectPageResponse = ProjectPage;

export interface GetProjectPageComponentListResponse {
  components: PlatformComponent[];
}

export interface GetPaypalConnectLinkParams {
  projectSlug: string;
}

export interface GetPaypalConnectLinkResponse {
  redirect_url: string;
}

export interface DisconnectPaypalAccountParams {
  projectSlug: string;
}

export interface DisconnectPaypalAccountResponse {
  message: string;
}

export interface UpdateProjectResponse {
  ok: string;
}

export interface UpdateProjectParams {
  projectSlug: string;
  data: Project;
}

export interface GetPlatformsParams {
  projectSlug: string;
}

export interface GetPlatformsResponse {
  applications: Application[];
  providers: OfferPlanProvider[];
  retailers: OfferPlanRetailer[];
  uuid: string;
}

export interface Application {
  android: string;
  ios: string;
}

export interface OfferPlanProvider {
  payment_provider: "stripe" | "paypal";
  provider_uid: string;
}

export interface OfferPlanRetailer {
  name: string;
  url_purchase: URL | null;
  url_purchase_management: URL | null;
  url_signin: URL | null;
  url_signout: URL | null;
  url_signup: URL | null;
  uuid: string;
}

export interface URL {
  authority: string;
  fragment: null;
  host: string;
  path: null;
  port: number;
  query: null;
  scheme: string;
  userinfo: null;
}

export interface GetComponentListParams {
  projectSlug: string;
  sectionList?: string[];
}

export interface UpdateComponentParams<T extends PlatformComponent> {
  projectSlug: string;
  component: T;
}

export type StorageProviderKind = "s3" | "s3_compatible";

export type StorageTestStatus = "untested" | "success" | "failed";

export interface StorageAccess {
  uuid: string;
  label: string;
  type: StorageProviderKind;
  bucket_url: string;
  region: string;
  host: string | null;
  access_key: string;
  secret_key_set: boolean;
  default: boolean;
  last_test_status: StorageTestStatus;
  last_tested_at: string | null;
  inserted_at: string;
  updated_at: string;
}

export interface GetStorageAccessListResponse {
  type: string;
  data: StorageAccess[];
  page?: { total_items: number };
}

export interface StorageAccessOption {
  uuid: string;
  label: string;
}

export interface GetStorageAccessOptionListResponse {
  type: string;
  data: StorageAccessOption[];
  indexes?: { uuid: Record<string, number> };
  page?: { total_items: number };
}

export interface StorageAccessPayload extends Pick<
  StorageAccess,
  "label" | "type" | "bucket_url" | "region" | "host" | "access_key" | "default"
> {
  secret_key?: string;
}

export interface CreateStorageAccessResponse {
  uuid: string;
}

export interface UpdateStorageAccessParams {
  uuid: string;
  data: StorageAccessPayload;
}

export interface TestStorageAccessConnectionParams {
  uuid: string;
  data?: StorageAccessPayload;
}

export interface EmbedProfile {
  uuid: string;
  label: string;
  domains: string[];
  default: boolean;
  inserted_at: string;
  updated_at: string;
}

export interface EmbedProfileOption {
  uuid: string;
  label: string;
  default: boolean;
}

export interface GetEmbedProfileListResponse {
  type: string;
  data: EmbedProfile[];
  page?: { total_items: number };
}

export interface GetEmbedProfileOptionListResponse {
  type: string;
  data: EmbedProfileOption[];
  indexes?: { uuid: Record<string, number> };
  page?: { total_items: number };
}

export type EmbedProfilePayload = Pick<
  EmbedProfile,
  "label" | "domains" | "default"
>;

export interface CreateEmbedProfileResponse {
  uuid: string;
}

export interface UpdateEmbedProfileParams {
  uuid: string;
  data: EmbedProfilePayload;
}

export interface EmbedProfileUsage {
  medias_count: number;
  inherited_medias_count: number;
}
